// Central SMS notification service (frontend). Routes ALL sends through the
// backend (which holds the Message91 credentials). The frontend never talks to
// Message91 directly and never sees the auth key / template IDs.
import { auth, db } from '../../integrations/firebase';
import { ref, get } from 'firebase/database';
import { NOTIFICATION_EVENTS } from './events';

const PROXY_BASE = (import.meta.env.VITE_PROXY_URL || (import.meta.env.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');

// Fetch backend SMS config status (booleans only — never secrets).
export const getSmsConfigStatus = async () => {
    let token = '';
    try { token = (await auth.currentUser?.getIdToken?.()) || ''; } catch (e) { /* ignore */ }
    try {
        const res = await fetch(`${PROXY_BASE}/api/notifications/sms/config-status`, {
            headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (!res.ok) return { error: true, authKeyConfigured: false, senderIdConfigured: false, templatesConfigured: [] };
        return await res.json();
    } catch (e) {
        return { error: true, authKeyConfigured: false, senderIdConfigured: false, templatesConfigured: [] };
    }
};

// Is an event enabled? Admin can disable per-event in settings/notifications/sms/events.
export const isEventEnabled = async (eventKey) => {
    try {
        const snap = await get(ref(db, `settings/notifications/sms`));
        if (!snap.exists()) return true; // default enabled until configured
        const cfg = snap.val();
        if (cfg.enabled === false) return false;
        const ev = cfg.events && cfg.events[eventKey];
        return ev === undefined ? true : ev !== false;
    } catch (e) {
        return true;
    }
};

/**
 * Send an SMS for a notification event through the backend.
 * @returns {Promise<{status:'sent'|'failed'|'skipped'|'not_configured'|'disabled', providerMessageId?, errorMessage?}>}
 */
export const sendSms = async ({ eventKey, mobile, variables = {}, relatedType, relatedId }) => {
    if (!NOTIFICATION_EVENTS[eventKey]) {
        return { status: 'failed', errorMessage: `Unknown event "${eventKey}"` };
    }
    if (!(await isEventEnabled(eventKey))) {
        return { status: 'disabled', errorMessage: 'Event disabled in settings' };
    }
    let token = '';
    try { token = (await auth.currentUser?.getIdToken?.()) || ''; } catch (e) { /* ignore */ }

    try {
        const res = await fetch(`${PROXY_BASE}/api/notifications/sms/send`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ eventKey, mobile, variables, relatedType, relatedId }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.status === 503) return { status: 'not_configured', errorMessage: data.errorMessage || 'SMS gateway not configured' };
        return data;
    } catch (e) {
        return { status: 'failed', errorMessage: e.message };
    }
};
